/**
 * Image Product Carousel Section for Product Image Display
 * 
 * An advanced image carousel component designed specifically for product pages,
 * featuring touch/swipe navigation, keyboard controls, lazy loading, and
 * responsive design optimized for e-commerce product visualization.
 * 
 * Core Features:
 * - Touch/swipe gesture navigation for mobile devices
 * - Keyboard navigation support (arrow keys)
 * - Progressive image loading for performance optimization
 * - Responsive thumbnail navigation grid
 * - Full-screen viewing capabilities
 * - Client-side hydration handling for SSR compatibility
 * 
 * Navigation Controls:
 * - Touch: Swipe left/right gestures on mobile
 * - Keyboard: Left/right arrow keys for navigation
 * - Thumbnails: Click any thumbnail to jump to that image
 * - Maximize: Full-screen view integration (external modal)
 * 
 * Performance Optimizations:
 * - Lazy loading strategy - only loads visible and adjacent images
 * - Progressive image marking to avoid duplicate loading
 * - Intersection Observer for thumbnail lazy loading
 * - Efficient state management for navigation
 * - Touch event debouncing to prevent multiple triggers
 * 
 * Responsive Design:
 * - Mobile-first approach with touch optimization
 * - Responsive thumbnail grid (2-4 columns)
 * - Adaptive image sizing for different screen sizes
 * - Swipe threshold tuning for mobile usability
 * 
 * Visual Features:
 * - Smooth transition animations between images
 * - Visual indicators for current image position
 * - Stock status overlay for out-of-stock products
 * - Professional image presentation with proper aspect ratios
 * - Hover effects and visual feedback
 * 
 * Accessibility:
 * - Keyboard navigation support
 * - Screen reader compatible navigation
 * - Semantic HTML structure for image galleries
 * - Descriptive alt text for all images
 * - Focus management for keyboard users
 * 
 * @param {Array} images - Array of product images from Sanity CMS
 * @param {string} title - Product title for image alt text (default: 'Product')
 * @param {boolean} outOfStock - Whether product is out of stock for overlay display
 * 
 * @description Advanced product image carousel with touch navigation and performance optimization
 */

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { Maximize2 } from 'lucide-react';

import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';

const ImageProductCarouselSection = ({ 
  images = [], 
  title = 'Product',
  outOfStock = false
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [loadedImages, setLoadedImages] = useState([0]); // Start with first image loaded
  const [isClient, setIsClient] = useState(false);
  
  const carouselRef = useRef(null);
  const imageContainerRef = useRef(null);
  
  // Check if we're on the client
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // Handle image navigation
  const nextImage = () => {
    const nextIndex = currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(nextIndex);
    
    // Mark the image as loaded
    if (!loadedImages.includes(nextIndex)) {
      setLoadedImages([...loadedImages, nextIndex]);
    }
  };

  const prevImage = () => {
    const prevIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    
    // Mark the image as loaded
    if (!loadedImages.includes(prevIndex)) {
      setLoadedImages([...loadedImages, prevIndex]);
    }
  };
  
  // Handle keyboard navigation
  useEffect(() => {
    if (!isClient) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, isClient]);
  
  // Add intersection observer for lazy loading thumbnails
  useEffect(() => {
    if (!isClient || !carouselRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Preload the next couple of images when carousel is visible
          const nextToLoad = [];
          const currentIdx = currentImageIndex;
          
          // Load next 2 images
          for (let i = 1; i <= 2; i++) {
            const idx = (currentIdx + i) % images.length;
            if (!loadedImages.includes(idx)) {
              nextToLoad.push(idx);
            }
          }
          
          if (nextToLoad.length > 0) {
            setLoadedImages([...loadedImages, ...nextToLoad]);
          }
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(carouselRef.current);
    
    return () => {
      if (carouselRef.current) {
        observer.unobserve(carouselRef.current);
      }
    };
  }, [currentImageIndex, loadedImages, images.length, isClient]);
  
  // Handle touch events for swipe on mobile
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
    
    // Prevent default to stop page scrolling during swipe
    e.preventDefault();
  };
  
  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 50) {
      nextImage(); // Swipe left
    } else if (touchEndX - touchStartX > 50) {
      prevImage(); // Swipe right
    }
  };
  
  // Prevent scrolling on the carousel itself
  const preventScroll = (e) => {
    e.preventDefault();
  };
  
  return (
    <div 
      ref={carouselRef}
      className="flex flex-col h-full w-full overflow-hidden fixed-carousel bg-white"
      style={{ position: 'relative' }}
    >
      {images.length > 0 ? (
        <>
          <div 
            ref={imageContainerRef}
            className="relative flex justify-center items-center w-full overflow-hidden"
            style={{ 
              paddingTop: '56.25%', // 16:9 aspect ratio (9/16 = 0.5625)
              height: 'auto',
              width: '100%'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={preventScroll}
          >
            {/* Main carousel image */}
            <div className="absolute top-0 left-0 w-full h-full">
              {loadedImages.includes(currentImageIndex) && (
                <Image 
                  src={urlForImage(images[currentImageIndex]) || '/images/placeholder.jpg'} 
                  alt={images[currentImageIndex]?.alt || `${title} - Image ${currentImageIndex + 1}`}
                  fill
                  loader={sanityLoader}
                  priority={currentImageIndex === 0}
                  className="object-cover"
                  sizes="100vw"
                />
              )}
            </div>
            
            {/* Status badges */}
            {outOfStock && (
              <div className="absolute top-4 left-4 bg-black text-white px-3 py-1 rounded-lg text-sm z-10">
                Temporarily unavailable
              </div>
            )}
          </div>
          
          {/* Thumbnail navigation - at bottom of carousel, always visible */}
          <div className="flex justify-center gap-2 py-6 overflow-x-auto scrollbar-thin bg-white z-20 relative">
            {images.map((image, index) => (
              <button
                key={index}
                className={`relative flex-none w-24 h-16 rounded overflow-hidden transition-opacity duration-300 ${
                  index === currentImageIndex ? 'border-2 border-gray-800 opacity-100' : 'border-2 border-transparent opacity-60 hover:opacity-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                  if (!loadedImages.includes(index)) {
                    setLoadedImages([...loadedImages, index]);
                  }
                }}
                aria-label={`View image ${index + 1}`}
              >
                <Image 
                  src={urlForImage(image) || '/images/placeholder.jpg'} 
                  alt={image?.alt || `${title} thumbnail ${index + 1}`}
                  fill
                  loader={sanityLoader}
                  className="object-cover"
                  sizes="144px"
                  loading="lazy"
                  unoptimized // Small thumbnails don't need Vercel optimization (saves cache reads)
                />
              </button>
            ))}
          </div>
          
          {/* Mobile dots indicators */}
          <div className="md:hidden flex justify-center gap-2 py-4 bg-white z-20 relative mt-2">
            {images.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-black w-4' 
                    : 'bg-gray-400'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                  if (!loadedImages.includes(index)) {
                    setLoadedImages([...loadedImages, index]);
                  }
                }}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
          <p>No images available</p>
        </div>
      )}
    </div>
  );
};

export default ImageProductCarouselSection; 